"use client"

// Modal for adding new contacts
import type React from "react"
import { useState, useEffect } from "react"
import axios from "axios"
import { X } from "lucide-react"
import { useAuthStore } from "../store/authStore"
import { env } from "../config/env"
import { toast } from "sonner"

interface AddContactModalProps {
  onClose: () => void
  onContactAdded: () => void
}

interface UserSearchResult {
  username: string
  name: string
  publicKeyX: string
  publicKeyY: string
}

const AddContactModal: React.FC<AddContactModalProps> = ({ onClose, onContactAdded }) => {
  const API_URL = env.API_URL
  const { token } = useAuthStore()
  const [contactUsername, setContactUsername] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [existingContacts, setExistingContacts] = useState<string[]>([])

  // Fetch existing contacts
  useEffect(() => {
    const fetchExistingContacts = async () => {
      try {
        const response = await axios.get(`${API_URL}/users/contacts/list`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const contactUsernames = response.data.data.map((c: { contactUsername: string }) => c.contactUsername)
        setExistingContacts(contactUsernames)
      } catch (error) {
        console.error("Failed to fetch existing contacts:", error)
        toast.error("Error getting list your contacts", {
          description: `Error fetching contacts: ${error}`
        })
      }
    }

    fetchExistingContacts()
  }, [token, API_URL])

  // Debounced search function
  useEffect(() => {
    const searchUsers = async () => {
      if (!contactUsername.trim() || contactUsername.length < 2) {
        setSearchResults([])
        return
      }

      setSearching(true)
      try {
        const response = await axios.get(`${API_URL}/users/search?q=${encodeURIComponent(contactUsername)}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        // Don't add user already in contact
        const filteredResults = (response.data.data || []).filter(
          (user: UserSearchResult) => !existingContacts.includes(user.username),
        )
        setSearchResults(filteredResults)
      } catch (error) {
        console.error("Failed to search users:", error)
        toast.error("Error finding users", {
          description: `Error fetching users: ${error}`
        })
        setSearchResults([])
      } finally {
        setSearching(false)
      }
    }

    const timeoutId = setTimeout(searchUsers, 700)
    return () => clearTimeout(timeoutId)
  }, [contactUsername, token, API_URL, existingContacts])

  const handleAddContact = async (username: string) => {
    setError("")
    setLoading(true)

    try {
      await axios.post(
        `${API_URL}/users/contacts/add`,
        { contactUsername: username },
        { headers: { Authorization: `Bearer ${token}` } },
      )

      toast.success("Contact added successfully!")

      window.dispatchEvent(new Event("refreshContacts"))

      onContactAdded()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      setError(error.response?.data?.error || "Failed to add contact")
      toast.error("Failed to add contact", {
        description: `Failed to add contact of ${username}`,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (contactUsername.trim()) {
      handleAddContact(contactUsername.trim())
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className="bg-slate-800 rounded-lg p-4 sm:p-6 w-full max-w-md border border-slate-700 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg sm:text-xl font-bold text-white">Add Contact</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition cursor-pointer" type="button">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-300 px-4 py-3 rounded mb-4">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          <div className="relative">
            <label className="block text-slate-300 text-sm font-medium mb-2">Search Users</label>
            <input
              type="text"
              value={contactUsername}
              onChange={(e) => setContactUsername(e.target.value)}
              placeholder="Type username to search..."
              className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
              disabled={loading}
              autoComplete="off"
            />

            {/* Search Results Dropdown */}
            {contactUsername.trim().length >= 2 && (
              <div className="absolute z-10 w-full mt-1 bg-slate-700 border border-slate-600 rounded shadow-lg max-h-60 overflow-y-auto">
                {searching ? (
                  <div className="px-4 py-3 text-slate-400 text-sm">Searching...</div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((user) => (
                    <button
                      key={user.username}
                      type="button"
                      onClick={() => handleAddContact(user.username)}
                      disabled={loading}
                      className="w-full text-left px-3 sm:px-4 py-2.5 sm:py-3 hover:bg-slate-600 transition cursor-pointer border-b border-slate-600 last:border-b-0 disabled:opacity-50"
                    >
                      <div className="text-white font-medium text-sm sm:text-base">{user.name}</div>
                      <div className="text-slate-400 text-xs sm:text-sm">@{user.username}</div>
                    </button>
                  ))
                ) : (
                  <div className="px-3 sm:px-4 py-3 text-slate-400 text-sm">No users found</div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={loading || !contactUsername.trim()}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white font-semibold py-2 text-sm sm:text-base rounded transition cursor-pointer"
            >
              {loading ? "Adding..." : "Add"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-slate-700 hover:bg-slate-600 text-slate-300 font-semibold py-2 text-sm sm:text-base rounded transition cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddContactModal
